import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    useCreateAdminNewsMutation,
    useGetAdminNewsCategoriesQuery,
    useUploadAdminNewsImageMutation,
} from "../store/api";
import {
    CreateAdminNewsRequestBody,
    CreateAdminNewsRequest,
} from "../api/news";
import NewsEditor from "../components/news/NewsEditor";
import { useNotification } from "../hooks/useNotification";
import Notification from "../components/ui/Notification";

const AdminNewsCreatePage: React.FC = () => {
    const navigate = useNavigate();
    const [createNews, { isLoading }] = useCreateAdminNewsMutation();
    const { data: categories } = useGetAdminNewsCategoriesQuery();
    const [uploadImage] = useUploadAdminNewsImageMutation();
    const { showError, showSuccess, notification, hideNotification } = useNotification();

    const languages = useMemo(() => ["ru", "en", "ka", "uk"], []);
    const [activeLang, setActiveLang] = useState<string>(languages[0]);
    const [titles, setTitles] = useState<Record<string, string>>({ ru: "" });
    const [bodyHtml, setBodyHtml] = useState<Record<string, string>>({ ru: "" });
    const [excerpt, setExcerpt] = useState<Record<string, string>>({ ru: "" });
    const [seoTitle, setSeoTitle] = useState<Record<string, string>>({ ru: "" });
    const [seoDescription, setSeoDescription] = useState<Record<string, string>>({
        ru: "",
    });
    const [seoKeywords, setSeoKeywords] = useState<Record<string, string>>({
        ru: "",
    });
    const [videoUrl, setVideoUrl] = useState<string>("");
    const [posterFile, setPosterFile] = useState<File | null>(null);
    const [slug, setSlug] = useState<string>("");
    const [newsCategoryId, setNewsCategoryId] = useState<number | "">("");
    const [isPublished, setIsPublished] = useState<boolean>(true);
    const [showPoster, setShowPoster] = useState<boolean>(false);

    const handleTitleChange = (value: string) => {
        setTitles((prev) => ({ ...prev, [activeLang]: value }));
    };

    const handleUploadImage = async (file: File) => {
        const resp = await uploadImage(file).unwrap();
        return resp.url;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            let payload: CreateAdminNewsRequestBody;

            const translations = Object.fromEntries(
                languages.map((l) => [
                    l,
                    {
                        title: titles[l] || "",
                        excerpt: excerpt[l] || null,
                        body_html: bodyHtml[l] || "",
                        seo_title: seoTitle[l] || null,
                        seo_description: seoDescription[l] || null,
                        seo_keywords: seoKeywords[l] || null,
                    },
                ])
            );

            if (posterFile) {
                // Используем FormData для отправки файла
                payload = new FormData();
                payload.append("translations", JSON.stringify(translations));
                if (slug) payload.append("slug", slug);
                if (newsCategoryId) {
                    payload.append("news_category_id", String(newsCategoryId));
                }
                payload.append("is_published", isPublished ? "1" : "0");
                payload.append("show_poster", showPoster ? "1" : "0");
                if (videoUrl) payload.append("alny_news_video_url", videoUrl);
                payload.append("alny_news_poster", posterFile);
            } else {
                // Обычная JSON отправка
                payload = {
                    slug: slug || null,
                    news_category_id: newsCategoryId ? Number(newsCategoryId) : null,
                    is_published: isPublished,
                    show_poster: showPoster,
                    alny_news_video_url: videoUrl || null,
                    translations,
                } as CreateAdminNewsRequest;
            }

            await createNews(payload).unwrap();
            showSuccess("Новость успешно создана");
            setTimeout(() => {
                navigate("/dashboard/news");
            }, 1000);
        } catch (err: any) {
            console.error(err);

            // Обработка ошибок валидации
            if (err?.data?.errors) {
                const validationErrors = err.data.errors;
                const errorMessages: string[] = [];

                // Собираем все ошибки валидации
                Object.keys(validationErrors).forEach((field) => {
                    const fieldErrors = validationErrors[field];
                    if (Array.isArray(fieldErrors)) {
                        fieldErrors.forEach((error: string) => {
                            errorMessages.push(`${field}: ${error}`);
                        });
                    } else if (typeof fieldErrors === "string") {
                        errorMessages.push(`${field}: ${fieldErrors}`);
                    }
                });

                if (errorMessages.length > 0) {
                    showError(errorMessages.join("\n"));
                } else {
                    showError("Ошибка валидации данных");
                }
            } else if (err?.data?.message) {
                showError(err.data.message);
            } else if (err?.message) {
                showError(err.message);
            } else {
                showError("Произошла ошибка при создании новости");
            }
        }
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-semibold">Создание новости</h1>
                <button
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded"
                    onClick={() => navigate(-1)}
                >
                    Назад
                </button>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                    <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
                        <nav
                            className="-mb-px flex space-x-4"
                            aria-label="Tabs"
                        >
                            {languages.map((lng) => (
                                <button
                                    key={lng}
                                    type="button"
                                    onClick={() => setActiveLang(lng)}
                                    className={`whitespace-nowrap py-2 px-3 border-b-2 text-sm font-medium ${
                                        activeLang === lng
                                            ? "border-blue-600 text-blue-600"
                                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                    }`}
                                >
                                    {lng.toUpperCase()}
                                </button>
                            ))}
                        </nav>
                    </div>

                    <div className="grid gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Заголовок ({activeLang})
                            </label>
                            <input
                                type="text"
                                className="w-full border rounded px-3 py-2"
                                value={titles[activeLang] || ""}
                                onChange={(e) =>
                                    handleTitleChange(e.target.value)
                                }
                                required={activeLang === "ru"}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Короткое описание ({activeLang})
                            </label>
                            <textarea
                                className="w-full border rounded px-3 py-2 h-24"
                                value={excerpt[activeLang] || ""}
                                onChange={(e) =>
                                    setExcerpt((prev) => ({
                                        ...prev,
                                        [activeLang]: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Текст (редактор) ({activeLang})
                            </label>
                            <NewsEditor
                                value={bodyHtml[activeLang] || ""}
                                onChange={(html) =>
                                    setBodyHtml((prev) => ({
                                        ...prev,
                                        [activeLang]: html,
                                    }))
                                }
                                onUploadImage={handleUploadImage}
                            />
                        </div>
                        <div className="grid md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    SEO title ({activeLang})
                                </label>
                                <input
                                    className="w-full border rounded px-3 py-2"
                                    value={seoTitle[activeLang] || ""}
                                    onChange={(e) =>
                                        setSeoTitle((prev) => ({
                                            ...prev,
                                            [activeLang]: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    SEO description ({activeLang})
                                </label>
                                <input
                                    className="w-full border rounded px-3 py-2"
                                    value={seoDescription[activeLang] || ""}
                                    onChange={(e) =>
                                        setSeoDescription((prev) => ({
                                            ...prev,
                                            [activeLang]: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div className="md:col-span-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    SEO keywords ({activeLang})
                                </label>
                                <input
                                    className="w-full border rounded px-3 py-2"
                                    value={seoKeywords[activeLang] || ""}
                                    onChange={(e) =>
                                        setSeoKeywords((prev) => ({
                                            ...prev,
                                            [activeLang]: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Slug (опционально)
                        </label>
                        <input
                            type="text"
                            className="w-full border rounded px-3 py-2"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Категория
                        </label>
                        <select
                            className="w-full border rounded px-3 py-2"
                            value={newsCategoryId}
                            onChange={(e) =>
                                setNewsCategoryId(
                                    e.target.value ? Number(e.target.value) : ""
                                )
                            }
                        >
                            <option value="">—</option>
                            {Array.isArray(categories) &&
                                categories.map((c: any) => (
                                    <option key={c.id} value={c.id}>
                                        {c.translations?.ru?.name || c.slug}
                                    </option>
                                ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            URL видео
                        </label>
                        <input
                            type="text"
                            className="w-full border rounded px-3 py-2"
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                        />
                    </div>
                    <label className="flex items-center space-x-3">
                        <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={isPublished}
                            onChange={(e) => setIsPublished(e.target.checked)}
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Опубликовано
                        </span>
                    </label>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Постер (до 5MB)
                        </label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) =>
                                setPosterFile(e.target.files?.[0] || null)
                            }
                        />
                        <label className="flex items-center space-x-3 mt-2">
                            <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={showPoster}
                                onChange={(e) =>
                                    setShowPoster(e.target.checked)
                                }
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                Показывать постер
                            </span>
                        </label>
                    </div>
                </div>

                <div className="flex items-center space-x-3">
                    <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
                        disabled={isLoading}
                    >
                        Создать
                    </button>
                    <button
                        type="button"
                        className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded"
                        onClick={() => navigate("/dashboard/news")}
                    >
                        Отмена
                    </button>
                </div>
            </form>

            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />
        </div>
    );
};

export default AdminNewsCreatePage;
